import { Injectable, computed, signal, type Signal } from '@angular/core';

import {
  AV_SYNC_FRONT_TRIM_SEC,
  AV_SYNC_TRIM_THRESHOLD_SEC,
  AV_SYNC_VIDEO_LEAD_COMPENSATION_SEC,
} from './av-sync.constants';
import {
  IDENTITY_AV_SYNC_WINDOW,
  type AvSyncVideoRange,
  type AvSyncWindow,
} from './av-sync.types';

/** Tolerance (sec) for the VSE-VSS vs videoDuration consistency check. */
const VIDEO_SPAN_MISMATCH_TOLERANCE_SEC = 0.15;

/**
 * Per-instance AV sync mapper. Inject by declaring it in the component's
 * `providers` array so each detail view gets its own state.
 *
 * PHASE38 (WAV-master): the WAV/CSV time axis is the single source of truth for
 * audio, waveform, and tags. This service no longer warps the wave timeline to
 * the video; it only computes the head offset used to slave the (muted) video
 * onto the wave axis. `videoTime = waveTime - videoOffsetSec`, no time-stretch.
 *
 * Offset resolution priority: CSV `VSS` → `VSE - videoDuration` → legacy fixed
 * front trim (only when the WAV is meaningfully longer) → 0.
 */
@Injectable()
export class AvSyncService {
  private readonly recordType = signal<string | null | undefined>(undefined);
  private readonly waveDurationSec = signal<number | null>(null);
  private readonly videoDurationSec = signal<number | null>(null);
  private readonly videoRange = signal<AvSyncVideoRange | null>(null);

  readonly window: Signal<AvSyncWindow> = computed(() => {
    const recordType = this.recordType();
    const waveDuration = this.waveDurationSec();
    const videoDuration = this.videoDurationSec();
    const explicitRange = this.videoRange();

    if (recordType !== 'VS' || videoDuration == null || videoDuration <= 0) {
      return IDENTITY_AV_SYNC_WINDOW;
    }

    const offset = this.resolveVideoOffset(
      explicitRange,
      waveDuration,
      videoDuration,
    );
    const start = offset;
    const end = offset + videoDuration;

    return {
      enabled: true,
      videoOffsetSec: offset,
      videoDurationSec: videoDuration,
      videoCoversStartSec: start,
      videoCoversEndSec: end,
      waveToVideo: (waveTime: number) =>
        clamp(waveTime - offset, 0, videoDuration),
      videoCovers: (waveTime: number) =>
        waveTime >= start - 1e-3 && waveTime <= end + 1e-3,
    };
  });

  setRecordType(recordType: string | null | undefined): void {
    this.recordType.set(recordType);
  }

  setWaveDuration(seconds: number | null): void {
    this.waveDurationSec.set(seconds);
  }

  setVideoDuration(seconds: number | null): void {
    this.videoDurationSec.set(seconds);
  }

  /**
   * Set an explicit per-record VS video range from CSV VSS/VSE markers.
   * Pass `null` when the CSV has no such markers.
   */
  setVideoRange(range: AvSyncVideoRange | null): void {
    this.videoRange.set(range);
  }

  /** Reset all state (e.g., when navigating to a new record). */
  reset(): void {
    this.recordType.set(undefined);
    this.waveDurationSec.set(null);
    this.videoDurationSec.set(null);
    this.videoRange.set(null);
  }

  /**
   * Resolve the head offset (raw WAV seconds at which the video begins).
   * Always returns a finite value >= 0 for a VS record with a known video
   * duration.
   */
  private resolveVideoOffset(
    range: AvSyncVideoRange | null,
    waveDuration: number | null,
    videoDuration: number,
  ): number {
    const maxOffset =
      waveDuration != null && waveDuration > 0
        ? Math.max(0, waveDuration - videoDuration)
        : Number.POSITIVE_INFINITY;

    const base = this.resolveBaseOffset(range, waveDuration, videoDuration);
    // Add a constant calibration to compensate for the muted video running
    // slightly ahead of the WAV. Applied uniformly to every offset source.
    return clamp(base + AV_SYNC_VIDEO_LEAD_COMPENSATION_SEC, 0, maxOffset);
  }

  /** Raw head offset before lead compensation. */
  private resolveBaseOffset(
    range: AvSyncVideoRange | null,
    waveDuration: number | null,
    videoDuration: number,
  ): number {
    if (range) {
      const vss = Number(range.waveStartSec);
      const vse = Number(range.waveEndSec);

      if (Number.isFinite(vss) && vss >= 0) {
        if (Number.isFinite(vse) && vse > vss) {
          const span = vse - vss;
          if (Math.abs(span - videoDuration) > VIDEO_SPAN_MISMATCH_TOLERANCE_SEC) {
            console.warn(
              `[AvSync] VSE-VSS (${span.toFixed(3)}s) differs from video duration ` +
                `(${videoDuration.toFixed(3)}s) by >${VIDEO_SPAN_MISMATCH_TOLERANCE_SEC}s; ` +
                'video drift may appear toward the tail.',
            );
          }
        }
        return Math.max(0, vss);
      }

      if (Number.isFinite(vse) && vse > 0) {
        return Math.max(0, vse - videoDuration);
      }
    }

    // Legacy fallback: no markers. Only apply the fixed front trim when the WAV
    // is meaningfully longer than the video; otherwise assume aligned (0).
    if (
      waveDuration != null &&
      waveDuration - videoDuration > AV_SYNC_TRIM_THRESHOLD_SEC
    ) {
      return AV_SYNC_FRONT_TRIM_SEC;
    }
    return 0;
  }
}

function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) return min;
  if (value < min) return min;
  if (value > max) return max;
  return value;
}
